interface ParsedDocumentName {
  labels: string[]
  clientName: string
  companyName: string
  isAccepted: boolean
}

/**
 * Parse a document filename following the naming convention:
 *   ${label1}[, ${label2}, ...] - ${clientName} - ${companyName} - Upload N.ext
 *   ${label1}[, ${label2}, ...] - ${clientName} - ${companyName} - Upload N - ok.ext
 *
 * Multiple labels within one file are separated by ", " (comma-space).
 * Structural segments (labels group, client, company, Upload) are separated by " - ".
 *
 * Returns the extracted labels (document types), client name, company name,
 * and whether the file has been accepted ("- ok" suffix).
 * Returns null if the filename doesn't match the expected structure.
 */
export function parseDocumentFileName(fileName: string): ParsedDocumentName | null {
  const segments = fileName.split(" - ")

  if (segments.length < 3) return null

  // Find the Upload segment index
  let uploadIdx = -1
  for (let i = 0; i < segments.length; i++) {
    if (segments[i].trim().toLowerCase().startsWith("upload ")) {
      uploadIdx = i
      break
    }
  }

  if (uploadIdx === -1) return null

  // Check for "- ok" acceptance: the segment after Upload would be "ok.ext"
  let isAccepted = false
  if (uploadIdx + 1 < segments.length) {
    const afterUpload = segments[uploadIdx + 1].trim().toLowerCase()
    if (/^ok\.\w+$/i.test(afterUpload)) {
      isAccepted = true
    }
  }

  // Segments before Upload: [...labelsGroup, clientName, companyName]
  const beforeUpload = segments.slice(0, uploadIdx)

  if (beforeUpload.length >= 3) {
    // Standard format: labelsGroup + client + company
    const companyName = beforeUpload[beforeUpload.length - 1].trim()
    const clientName = beforeUpload[beforeUpload.length - 2].trim()
    // Join remaining segments back with " - " then split by ", " for multi-labels
    const labelsString = beforeUpload.slice(0, beforeUpload.length - 2).map(s => s.trim()).join(" - ")
    const labels = labelsString.split(", ").map(s => s.trim())
    return { labels, clientName, companyName, isAccepted }
  } else if (beforeUpload.length === 2) {
    // No company name (legacy format): labelsGroup + client
    const clientName = beforeUpload[beforeUpload.length - 1].trim()
    const labelsString = beforeUpload.slice(0, beforeUpload.length - 1).map(s => s.trim()).join(" - ")
    const labels = labelsString.split(", ").map(s => s.trim())
    return { labels, clientName, companyName: "", isAccepted }
  }

  return null
}

/**
 * Check if a document's filename contains a specific doc type,
 * using structural parsing to avoid false positives from client/company names.
 * Falls back to startsWith for legacy filenames that don't match the expected structure.
 */
export function documentContainsDocType(fileName: string, docTypePrefixes: string[]): boolean {
  const parsed = parseDocumentFileName(fileName)

  if (parsed) {
    // Structural match: check if any prefix matches any extracted label
    return parsed.labels.some(label =>
      docTypePrefixes.some(prefix => label.toLowerCase() === prefix.toLowerCase())
    )
  }

  // Fallback for legacy filenames: use existing startsWith logic
  const name = fileName.toLowerCase()
  return docTypePrefixes.some(p => name.startsWith(p.toLowerCase()))
}

/**
 * Check if a document's filename contains a specific doc type AND is accepted ("- ok").
 * Falls back to startsWith + endsWith for legacy filenames.
 */
export function documentContainsAcceptedDocType(fileName: string, docTypePrefixes: string[]): boolean {
  const parsed = parseDocumentFileName(fileName)

  if (parsed) {
    if (!parsed.isAccepted) return false
    return parsed.labels.some(label =>
      docTypePrefixes.some(prefix => label.toLowerCase() === prefix.toLowerCase())
    )
  }

  // Fallback for legacy filenames
  const name = fileName.toLowerCase()
  const hasPrefix = docTypePrefixes.some(p => name.startsWith(p.toLowerCase()))
  if (!hasPrefix) return false
  const nameWithoutExt = fileName.replace(/\.[^.]+$/, "").toLowerCase()
  return nameWithoutExt.endsWith("- ok")
}
